import React from 'react';
import {
	MenuList,
	MenuListItem,
	MenuSection,
	MenuSectionHeading,
} from '@atlaskit/navigation-system';
import { useIntl } from '@atlassian/jira-intl';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { MENU_ID_PROJECTS_VIEW_ALL_STARRED } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { AnyAri } from '@atlassian/ari/any-ari';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import messages from './messages.tsx';
import { useDragAndDropReorderingAnalytics } from './drag-and-drop-reorder-analytics.tsx';

const VIEW_ALL_STARRED_HREF = '/jira/your-work?starred';

type FavoriteItemsType = {
	idSet: Set<string>;
	list: Array<React.JSX.Element>;
	hasItems: boolean;
	hasNextPage: boolean;
};

type StarredSectionWithFocusRestorationProps = {
	favoriteItems: FavoriteItemsType;
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	projectKey: string;
};

export function StarredSectionWithFocusRestoration(props: StarredSectionWithFocusRestorationProps) {
	const { favoriteItems, currentItems, currentOrder, projectKey } = props;
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useDragAndDropReorderingAnalytics();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();
	const cloudId = useCloudId();

	const ownerAri = IdentityUserAri.create({ userId: accountId || 'UNKNOWN' }).toString();
	const entityAri = AnyAri.create({
		cloudId,
		resourceType: 'project',
		resourceId: projectKey,
		resourceOwner: 'jira',
	}).toString();

	return (
		favoriteItems.hasItems && (
			<NestedItemDropTarget
				currentItems={currentItems}
				currentOrder={currentOrder}
				entityAri={entityAri}
				fireAnalyticsEvent={fireAnalyticsEvent}
				group="SIDE_NAVIGATION_PROJECT_BOARDS"
				ownerAri={ownerAri}
			>
				<MenuListItem testId="navigation-apps-sidebar-nav4-sidebars-content-projects-jsw-project-menu.ui.jsw-expandable-project.async-content.jsw-boards.starred-section">
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.starred)}</MenuSectionHeading>
						<MenuList>{favoriteItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
				{favoriteItems.hasNextPage && (
					<Nav4MenuLinkItem href={VIEW_ALL_STARRED_HREF} menuId={MENU_ID_PROJECTS_VIEW_ALL_STARRED}>
						{formatMessage(messages.viewAllStarred)}
					</Nav4MenuLinkItem>
				)}
			</NestedItemDropTarget>
		)
	);
}
